import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Settings,
  Pencil,
  Trash2,
  History,
  DollarSign,
  AlertTriangle,
  Loader2,
  CheckCircle,
  User,
  Calendar,
} from "lucide-react";
import { toast } from "sonner";

// Format helpers
const currency = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return dateStr.slice(0, 10);
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const dt = new Date(dateStr);
    return dt.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return dateStr.slice(0, 16).replace("T", " ");
  }
};

// Level badge
function LevelBadge({ level }) {
  const styles = {
    campaign: "bg-indigo-500/20 text-indigo-300",
    adset: "bg-blue-500/20 text-blue-300",
    ad: "bg-emerald-500/20 text-emerald-300",
    form: "bg-amber-500/20 text-amber-300",
  };
  return (
    <Badge className={`text-xs capitalize ${styles[level] || "bg-slate-500/20 text-slate-300"}`}>
      {level}
    </Badge>
  );
}

// Edit Entry Modal
function EditEntryModal({ entry, isOpen, onClose, onSave }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    period_start: "",
    period_end: "",
    ad_spend: 0,
  });
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);

  useEffect(() => {
    if (entry) {
      setFormData({
        name: entry.name || "",
        period_start: entry.period_start || "",
        period_end: entry.period_end || "",
        ad_spend: entry.ad_spend || 0,
      });
      setErrors([]);
      setWarnings([]);
    }
  }, [entry]);

  const validateAndSave = async () => {
    const newErrors = [];
    const newWarnings = [];

    // Basic validation
    if (!formData.period_start) newErrors.push("Period start is required");
    if (!formData.period_end) newErrors.push("Period end is required");
    if (formData.ad_spend < 0) newErrors.push("Ad spend cannot be negative");

    // Date validation
    if (formData.period_start && formData.period_end) {
      const start = new Date(formData.period_start);
      const end = new Date(formData.period_end);
      if (start > end) {
        newErrors.push("Period start must be before period end");
      }
    }

    if (newErrors.length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors([]);

    try {
      // Check for overlaps via preview API
      const previewRes = await api.post("/ad-spend-entries/csv/preview", [
        {
          level: entry.level,
          meta_campaign_id: entry.meta_campaign_id,
          meta_adset_id: entry.meta_adset_id,
          meta_ad_id: entry.meta_ad_id,
          meta_form_id: entry.meta_form_id,
          period_start: formData.period_start,
          period_end: formData.period_end,
          ad_spend: formData.ad_spend,
        },
      ]);

      if (previewRes.data.has_overlaps) {
        // Filter out self-overlap
        const otherOverlaps = previewRes.data.overlap_warnings.filter(
          (w) => !w.includes(entry.id)
        );
        if (otherOverlaps.length > 0) {
          setWarnings(otherOverlaps);
        }
      }

      // Update entry
      await api.put(`/ad-spend-entries/${entry.id}`, {
        name: formData.name,
        period_start: formData.period_start,
        period_end: formData.period_end,
        ad_spend: parseFloat(formData.ad_spend),
      });

      toast.success("Entry updated successfully");
      onSave();
      onClose();
    } catch (err) {
      console.error("Update error:", err);
      setErrors([err.response?.data?.detail || "Failed to update entry"]);
    } finally {
      setLoading(false);
    }
  };

  if (!entry) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-slate-950 text-slate-100 ring-1 ring-white/10">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-100">
            <Pencil className="h-4 w-4 text-indigo-400" />
            Edit Spend Entry
          </DialogTitle>
          <DialogDescription asChild>
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <LevelBadge level={entry.level} />
              <span className="text-slate-400">{entry.meta_campaign_id}</span>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {errors.length > 0 && (
            <Alert className="bg-red-900/30 border-red-700">
              <AlertTriangle className="h-4 w-4 text-red-400" />
              <AlertDescription className="text-red-200">
                <ul className="list-disc list-inside">
                  {errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {warnings.length > 0 && (
            <Alert className="bg-amber-900/30 border-amber-700">
              <AlertTriangle className="h-4 w-4 text-amber-400" />
              <AlertDescription className="text-amber-200 text-sm">
                <p className="font-medium mb-1">Overlapping date ranges detected:</p>
                <ul className="list-disc list-inside">
                  {warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <label className="text-sm text-slate-300">Name</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="bg-white/5 border-white/10 text-slate-100"
              placeholder="Entry name"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm text-slate-300">Period Start</label>
              <Input
                type="date"
                value={formData.period_start}
                onChange={(e) => setFormData({ ...formData, period_start: e.target.value })}
                className="bg-white/5 border-white/10 text-slate-100"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-slate-300">Period End</label>
              <Input
                type="date"
                value={formData.period_end}
                onChange={(e) => setFormData({ ...formData, period_end: e.target.value })}
                className="bg-white/5 border-white/10 text-slate-100"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm text-slate-300">Ad Spend ($)</label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={formData.ad_spend}
              onChange={(e) => setFormData({ ...formData, ad_spend: e.target.value })}
              className="bg-white/5 border-white/10 text-slate-100"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading} className="border-slate-600 text-slate-200 hover:bg-slate-800">
            Cancel
          </Button>
          <Button
            onClick={validateAndSave}
            disabled={loading}
            className="bg-indigo-500/90 text-white hover:bg-indigo-500"
          >
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Main Component
export default function SpendEntryManager({ onDataChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("entries");
  const [loading, setLoading] = useState(false);

  // Spend entries state
  const [entries, setEntries] = useState([]);
  const [entriesTotal, setEntriesTotal] = useState(0);

  // Import logs state
  const [logs, setLogs] = useState([]);

  // Edit state
  const [editingEntry, setEditingEntry] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);

  // Delete state
  const [deletingEntry, setDeletingEntry] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch data when modal opens
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [entriesRes, logsRes] = await Promise.all([
        api.get("/ad-spend-entries?limit=100"),
        api.get("/ad-spend-entries/import-logs?limit=20"),
      ]);
      setEntries(entriesRes.data.items || []);
      setEntriesTotal(entriesRes.data.total || 0);
      setLogs(logsRes.data.logs || []);
    } catch (err) {
      console.error("Failed to fetch data:", err);
      toast.error("Failed to load spend data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, fetchData]);

  // Handle edit
  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setIsEditOpen(true);
  };

  const handleEditSave = () => {
    fetchData();
    if (onDataChange) onDataChange();
  };

  // Handle delete
  const handleDelete = (entry) => {
    setDeletingEntry(entry);
    setIsDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingEntry) return;

    setDeleteLoading(true);
    try {
      await api.delete(`/ad-spend-entries/${deletingEntry.id}`);
      toast.success("Entry deleted successfully");
      setIsDeleteOpen(false);
      setDeletingEntry(null);
      fetchData();
      if (onDataChange) onDataChange();
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(err.response?.data?.detail || "Failed to delete entry");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="bg-white/5 text-slate-100 border-white/10 hover:bg-white/10"
            data-testid="manage-spend-button"
          >
            <Settings className="h-4 w-4 mr-2" />
            Manage Spend
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col bg-slate-950 text-slate-100 ring-1 ring-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-100">
              <DollarSign className="h-5 w-5 text-emerald-400" />
              Spend Entry Management
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              View, edit, and delete ad spend entries. View import history.
            </DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="bg-indigo-500/20 ring-1 ring-indigo-400/20 w-fit">
              <TabsTrigger value="entries" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100 text-indigo-200">
                <DollarSign className="h-3.5 w-3.5 mr-1.5" />
                Spend Entries ({entriesTotal})
              </TabsTrigger>
              <TabsTrigger value="history" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100 text-indigo-200">
                <History className="h-3.5 w-3.5 mr-1.5" />
                Import History
              </TabsTrigger>
            </TabsList>

            {/* Spend Entries Tab */}
            <TabsContent value="entries" className="flex-1 overflow-auto mt-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
                </div>
              ) : entries.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <DollarSign className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p>No spend entries yet.</p>
                  <p className="text-sm mt-1">Use "Import CSV" to add entries.</p>
                </div>
              ) : (
                <div className="border border-slate-700 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader className="bg-slate-800/50">
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-300">Level</TableHead>
                        <TableHead className="text-slate-300">Name</TableHead>
                        <TableHead className="text-slate-300">Period</TableHead>
                        <TableHead className="text-slate-300 text-right">Spend</TableHead>
                        <TableHead className="text-slate-300">Created</TableHead>
                        <TableHead className="text-slate-300 w-20"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entries.map((entry) => (
                        <TableRow key={entry.id} className="border-slate-700 hover:bg-white/5">
                          <TableCell>
                            <LevelBadge level={entry.level} />
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="text-slate-100 text-sm truncate max-w-[180px]">
                                {entry.name || "Unnamed"}
                              </p>
                              <p className="text-slate-500 text-xs font-mono">
                                {entry.meta_campaign_id?.slice(0, 12)}...
                              </p>
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-300 text-sm">
                            {formatDate(entry.period_start)} → {formatDate(entry.period_end)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-emerald-400">
                            {currency(entry.ad_spend)}
                          </TableCell>
                          <TableCell className="text-slate-400 text-xs">
                            {formatDateTime(entry.created_at)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-slate-400 hover:text-slate-100"
                                onClick={() => handleEdit(entry)}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-slate-400 hover:text-red-400"
                                onClick={() => handleDelete(entry)}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            {/* Import History Tab */}
            <TabsContent value="history" className="flex-1 overflow-auto mt-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
                </div>
              ) : logs.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <History className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p>No import history yet.</p>
                </div>
              ) : (
                <div className="border border-slate-700 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader className="bg-slate-800/50">
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-300">Imported At</TableHead>
                        <TableHead className="text-slate-300">User</TableHead>
                        <TableHead className="text-slate-300 text-right">Rows Imported</TableHead>
                        <TableHead className="text-slate-300 text-right">Rows Skipped</TableHead>
                        <TableHead className="text-slate-300 text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logs.map((log) => (
                        <TableRow key={log.id} className="border-slate-700 hover:bg-white/5">
                          <TableCell>
                            <div className="flex items-center gap-2 text-slate-300 text-sm">
                              <Calendar className="h-3.5 w-3.5 text-slate-500" />
                              {formatDateTime(log.imported_at)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 text-slate-300 text-sm">
                              <User className="h-3.5 w-3.5 text-slate-500" />
                              {log.user_email || log.user_id?.slice(0, 8)}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge className="bg-emerald-500/20 text-emerald-300">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              {log.imported_count}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {log.skipped_count > 0 ? (
                              <Badge className="bg-amber-500/20 text-amber-300">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                {log.skipped_count}
                              </Badge>
                            ) : (
                              <span className="text-slate-500">0</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono text-slate-300">
                            {log.total_rows}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="border-t border-slate-700 pt-4">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <EditEntryModal
        entry={editingEntry}
        isOpen={isEditOpen}
        onClose={() => {
          setIsEditOpen(false);
          setEditingEntry(null);
        }}
        onSave={handleEditSave}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-400" />
              Delete Spend Entry?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the spend entry:
              <br />
              <span className="font-medium text-slate-300">
                {deletingEntry?.name || "Unnamed"} ({deletingEntry?.level})
              </span>
              <br />
              <span className="text-emerald-400 font-mono">
                {currency(deletingEntry?.ad_spend)}
              </span>
              <br />
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleteLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
